import React, { useEffect } from "react";
import { Modal, Form, Input, Row, Col, Card, Select, Avatar } from "antd";
import { PersonnelService } from "../../services/PersonnelService";
import { catchError } from "../../services/DaoService";
import Toast from "../../helpers/Toast";
import PropTypes from "prop-types";
import CustomFormItem from "../utils/form/CustomFormItem";
import { DepartementService } from "../../services/DepartementService";
import PhoneInput from "react-phone-number-input";
import { MailOutlined } from "@ant-design/icons";

const PersonnelEditForm = ({ visible, uid, onUpdate, onCancel }) => {
  const [form] = Form.useForm();
  const [editing, setEditing] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({});

  const [loading, setLoading] = React.useState(false);
  const [personnels, setPersonnels] = React.useState([]);
  const [departements, setDepartements] = React.useState([]);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      PersonnelService.getInstance().all(),
      DepartementService.getInstance().all()
    ]).then(([personnelData, departementData]) => {
      setPersonnels(personnelData.data);
      setDepartements(departementData.data);
    }).catch((error) => {
      catchError(error);
    }).finally(() => {
      setLoading(false);
    });
  }, [visible]);

  useEffect(() => {
    if (uid) {
      setEditing(true);
      PersonnelService.getInstance()
        .find(uid)
        .then((response) => {
          form.setFieldsValue(response.data);
        })
        .catch((error) => {
          catchError(error);
        })
        .finally(() => {
          setEditing(false);
        });
    }
  }, [uid, form]);

  const handleSave = () => {
    setEditing(true);
    form
      .validateFields()
      .then((values) => {
        PersonnelService.getInstance()
          .update(uid, values)
          .then((response) => {
            Toast.success(response.message);
            onUpdate(response.data);
          })
          .catch((error) => {
            const errors = error.response.data;
            if (errors.validation && Object.keys(errors?.errors).length) {
              setValidationErrors(errors.errors);
            } else {
              catchError(error);
            }
          })
          .finally(() => {
            setEditing(false);
          });
      })
      .catch(() => {
        setEditing(false);
      });
  };

  return (
    <Modal destroyOnHidden
      open={visible}
      onCancel={onCancel}
      okText="Enregistrer"
      onOk={handleSave}
      title="Modifier personnel"
      forceRender
    >
      <Card loading={editing} style={{ marginBottom: 20 }}>
        <Form
          layout="vertical"
          form={form}
          name="editPersonnelForm"
          initialValues={{
            enabled: 1,
          }}
        >
          <Row gutter={24}>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Prenoms"
                name="prenoms"
                rules={[{ required: true, message: "Veuillez saisir le prenoms!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="text" />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Nom"
                name="nom"
                rules={[{ required: true, message: "Veuillez saisir le nom!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="text" />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Email"
                name="email"
                rules={[{ required: true, message: "Veuillez saisir le email!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="email" suffix={<MailOutlined />} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Téléphone"
                name="telephone"
                rules={[{ required: true, message: "Veuillez saisir le telephone!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <PhoneInput defaultCountry="SN" placeholder="Saisir le numéro de téléphone" />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Supérieur hiérarchique"
                name="personnel_id"
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Select size="large" placeholder="Sélectionner un supérieur hiérarchique"
                  allowClear loading={loading}
                  showSearch
                  optionFilterProp="name" options={personnels.map((personnel) => ({
                    label: <>
                      <Avatar src={personnel.avatar_link} /> {personnel.name}
                    </>,
                    value: personnel.id,
                    name: `${personnel.name}`
                  }))} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Departement de rattachement"
                name="departement_id"
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Select placeholder="Sélectionner un département de rattachement"
                  allowClear loading={loading}
                  showSearch
                  optionFilterProp="label" options={departements.map((departement) => ({
                    label: `${departement.nom} (${departement.code})`,
                    value: departement.id,
                  }))} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem label="Type Agent" name="permanent" rules={[{ required: true, message: "Veuillez saisir le statut!" }]} validationErrors={validationErrors} setValidationErrors={setValidationErrors}>
                <Select placeholder="Sélectionner le statut" allowClear loading={loading} showSearch size="large" optionFilterProp="name" options={[{ label: "Permanent", value: 1 }, { label: "Temporaire", value: 0 }]} />
              </CustomFormItem>
            </Col>
          </Row>

        </Form>
      </Card>
    </Modal>
  );
};

export default PersonnelEditForm;

PersonnelEditForm.propTypes = {
  visible: PropTypes.bool,
  onUpdate: PropTypes.func,
  onCancel: PropTypes.func,
  uid: PropTypes.string.isRequired
};
